import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { format } from 'app/shared/utils/string.util';
import {
    AzureMLServiceLinkedService,
    azureMLServiceAuthTypes
} from 'app/model/linkedservice/azure-ml-service.linkedservice';
import { azureResourceSelectMethodOptions } from 'app/registries/consts/metadata-consts';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { compact } from 'lodash';

const workspaceSelectionSetting: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.AzureMLService_Workspace_SelectionMethod,
    description: stringResources.AzureMLService_Workspace_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    defaultValue: '{mlWorkspaceName != null ? "manual" : "azure"}',
    isLabelVertical: true,
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-ml-service-workspace-select',
                    bind: {
                        subscriptionId: 'subscriptionId',
                        resourceGroupName: 'resourceGroupName',
                        mlWorkspaceName: 'mlWorkspaceName'
                    },
                    showPEStatus: true,
                    validators: {
                        subscriptionId: [{ type: 'required', errorMessage: '' }],
                        resourceGroupName: [{ type: 'required', errorMessage: '' }],
                        mlWorkspaceName: [{ type: 'required', errorMessage: '' }]
                    }
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'subscriptionId',
                        label: stringResources.SubscriptionId,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'resourceGroupName',
                        label: stringResources.ModelName_ResourceGroupName,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'mlWorkspaceName',
                        label: stringResources.ModelName_MLWorkspaceName,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'private-endpoint-status'
                }
            ]
        }
    ]
};

const tenantTextBoxSetting: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'tenant-textbox',
        bind: 'tenant',
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        label: stringResources.ModelName_Tenant,
        required: true
    }
};

const servicePrincipalIDSetting: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    }
};

const servicePrincipalKeySetting: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'parameterizable',
        attribute: 'servicePrincipalKey',
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            isLabelVertical: true,
            validators: [{ type: 'required', errorMessage: '' }]
        }
    },
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureMLService_DisplayText),
        stringResources.ModelName_ServicePrincipalKey
    )
};

export const azureMLServiceMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(injector, 'ExposureControlService');
    const metadata: ILinkedServiceTypeMetadata = {
        type: Metamodel.modelFor(AzureMLServiceLinkedService).name,
        displayName: stringResources.AzureMLService_DisplayText,
        supportedInTrident: true,
        supportOPDG: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AzureMachineLearning' as ResourceKind
        },
        isKnownInPipelineContext: true,
        icon: {
            className: 'svg-i-100x100-azure_ml'
        },
        category: ['Azure'],
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.ServicePrincipal,
            exposureControlService.enableWIForCustomMLActivities ? ConnectionAuthKind.WorkspaceIdentity : undefined
        ]),
        isCompute: true,
        isFirstClass: true,
        supportPE: true,
        peGroupIDs: ['amlworkspace'],
        controls: [
            {
                type: 'select-switch',
                bind: 'authenticationMethod',
                label: stringResources.Authentication_Method_Label,
                options: [
                    {
                        label: stringResources.AuthenticationType_ManagedServiceIdentity,
                        value: azureMLServiceAuthTypes.MSIAuthentication
                    },
                    {
                        label: stringResources.AuthenticationType_ServicePrincipal,
                        value: azureMLServiceAuthTypes.ServicePrincipalAuthentication
                    }
                ],
                isLabelVertical: true,
                defaultValue: azureMLServiceAuthTypes.MSIAuthentication,
                cases: [
                    {
                        caseValue: azureMLServiceAuthTypes.MSIAuthentication,
                        children: [
                            workspaceSelectionSetting,
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureMLService_DisplayText,
                                link: 'https://go.microsoft.com/fwlink/?linkid=2164350'
                            }
                        ]
                    },
                    {
                        caseValue: azureMLServiceAuthTypes.ServicePrincipalAuthentication,
                        children: [
                            workspaceSelectionSetting,
                            tenantTextBoxSetting,
                            servicePrincipalIDSetting,
                            servicePrincipalKeySetting
                        ]
                    }
                ]
            }
        ],
        copySinkTypes: [],
        copySourceTypes: []
    };
    return metadata;
};
